/** BIBLIOTECA DE SÍMBOLOS — página del menú principal.
 *  Muestra los símbolos técnicos integrados (SVG) y permite agregar símbolos personalizados (con imagen). */
import React, { useEffect, useRef, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, Trash, UploadSimple } from '@phosphor-icons/react';
import { SIMBOLOS, renderSimbolo } from '@/components/sa/simbolos/biblioteca';

export default function BibliotecaSimbolos() {
  const [custom, setCustom] = useState([]);
  const [form, setForm] = useState({ nombre: '', categoria: 'general' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get('/sa/simbolos').then((r) => setCustom(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const integrados = Object.entries(SIMBOLOS).filter(([, v]) => v.render);
  const porCat = {};
  integrados.forEach(([sid, v]) => { (porCat[v.categoria || 'general'] = porCat[v.categoria || 'general'] || []).push([sid, v]); });

  const guardar = async () => {
    if (!form.nombre.trim() || !file) return toast.error('Nombre e imagen requeridos');
    setSaving(true);
    const fd = new FormData();
    fd.append('nombre', form.nombre.trim());
    fd.append('categoria', form.categoria.trim() || 'general');
    fd.append('file', file);
    try {
      await api.post('/sa/simbolos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Símbolo "${form.nombre}" agregado — ya aparece en el picker del Diseñador 2D`);
      setForm({ nombre: '', categoria: 'general' });
      setFile(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); }
    finally { setSaving(false); }
  };

  const borrar = async (s) => {
    try { await api.delete(`/sa/simbolos/${s.id}`); toast.success(`"${s.nombre}" eliminado`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="p-4 space-y-4 max-w-5xl" data-testid="biblioteca-simbolos">
      <div>
        <h1 className="text-xl font-black text-[var(--text)]">Biblioteca de Símbolos Soluarq</h1>
        <p className="text-xs text-[var(--text-muted)]">Se usan al diseñar en el <b>Modulador de duchas</b> (botón "+ Insertar herraje") y en el <b>Diseñador 2D</b> (botón "Símbolos"). Los personalizados que agregues aquí quedan disponibles en el picker del Diseñador 2D.</p>
      </div>

      <Card className="p-3 space-y-2" data-testid="simbolos-nuevo">
        <p className="text-xs font-black uppercase text-[#0EA5E9]">Agregar símbolo personalizado</p>
        <div className="flex flex-wrap gap-2 items-center">
          <Input placeholder="Nombre (ej. Araña 4 brazos)" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="h-9 text-xs w-56" data-testid="simbolo-nombre" />
          <Input placeholder="Categoría (ej. fachadas)" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="h-9 text-xs w-40" data-testid="simbolo-categoria" />
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => fileRef.current?.click()} data-testid="simbolo-file-btn">
            <UploadSimple size={14} className="mr-1" /> {file ? file.name.slice(0, 22) : 'Elegir imagen (PNG/JPG/SVG)'}
          </Button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="simbolo-file-input" />
          <Button size="sm" className="h-9 text-xs bg-[#0EA5E9] hover:bg-[#0284C7] text-white" onClick={guardar} disabled={saving} data-testid="simbolo-guardar">
            <Plus size={14} className="mr-1" /> {saving ? 'Guardando…' : 'Agregar'}
          </Button>
        </div>
      </Card>

      {custom.length > 0 && (
        <Card className="p-3 space-y-2" data-testid="simbolos-custom">
          <p className="text-xs font-black uppercase text-emerald-600">Personalizados ({custom.length})</p>
          <div className="flex flex-wrap gap-2">
            {custom.map((s) => (
              <div key={s.id} className="w-24 border border-[var(--border)] rounded-md p-1.5 text-center relative group" data-testid={`simbolo-custom-${s.id}`}>
                <img src={`${api.defaults.baseURL}/sa/catalogo/imagen/${s.imagen_path}`} alt={s.nombre} className="w-full h-14 object-contain" />
                <p className="text-[9px] font-bold text-[var(--text)] leading-tight mt-1">{s.nombre}</p>
                <p className="text-[8px] text-[var(--text-muted)]">{s.categoria}</p>
                <button onClick={() => borrar(s)} className="absolute top-0.5 right-0.5 text-red-400 opacity-0 group-hover:opacity-100" title="Eliminar" data-testid={`simbolo-del-${s.id}`}><Trash size={13} /></button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {Object.entries(porCat).map(([cat, arr]) => (
        <Card key={cat} className="p-3 space-y-2">
          <p className="text-xs font-black uppercase text-[var(--text-muted)]">{cat} ({arr.length}) — integrados</p>
          <div className="flex flex-wrap gap-2">
            {arr.map(([sid, v]) => (
              <div key={sid} className="w-24 border border-[var(--border)] rounded-md p-1.5 text-center" title={v.nombre} data-testid={`simbolo-int-${sid}`}>
                <svg width="60" height="52" viewBox="-175 -175 350 350" className="mx-auto">{renderSimbolo(sid, '#B45309')}</svg>
                <p className="text-[9px] font-bold text-[var(--text)] leading-tight">{v.nombre}</p>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
